import { API_URL } from 'config';

import { makeAPICall } from './api';

export declare namespace API {
    export interface Group {
        id: number;
        name: string;
    }

    export interface Organisation {
        id: number;
        name: string;
    }

    export interface UserProfile {
        connection_id: string | null;
        allow_emails: boolean;
    }

    export interface User {
        id: number;
        username: string;
        email: string;
        groups: Group[];
        organisation_set: Organisation[];
        first_name: string;
        last_name: string;
        is_active: boolean;
        is_staff: boolean;
        is_superuser: boolean;
        profile: UserProfile;
    }

    export interface CommandResponse {
        success: boolean;
        error?: string;
    }
}

export type Group =
    | 'Organisation Manager'
    | 'Ground Station Operator'
    | 'Satellite Operator';

export interface UserProfile {
    connectionId: string | null;
    allowEmails: boolean;
}

export interface User {
    id: number;
    fullName: string;
    userName: string;
    firstName: string;
    lastName: string;
    email: string;
    profile: UserProfile;
    groups: Group[];
    organisation_set: API.Organisation[];
    isActive: boolean;
    isStaff: boolean;
    isSuperuser: boolean;
}

const SELF_ROUTE = '/users/self/';

const USERS_ROUTE = '/users';

const SET_PASSWORD_ROUTE = '/users/set_password/';

export const getSelf = makeAPICall<User, void, API.User>(
    () => ({
        url: `${API_URL}${SELF_ROUTE}`,
    }),
    ({
        id,
        email,
        first_name,
        last_name,
        groups,
        is_active,
        is_staff,
        is_superuser,
        organisation_set,
        username,
        profile,
    }) => ({
        id,
        userName: username,
        fullName: `${first_name} ${last_name}`,
        firstName: first_name,
        lastName: last_name,
        email,
        groups: groups.map((g) => g.name as Group),
        organisation_set,
        isActive: is_active,
        isStaff: is_staff,
        isSuperuser: is_superuser,
        profile: {
            connectionId: profile.connection_id,
            allowEmails: profile.allow_emails,
        },
    }),
);

export interface UpdatePassword {
    old_password: string;
    password: string;
    confirm_password: string;
}

export interface EmailPreferences {
    allowEmails: boolean;
}

export const updateEmailPreference = makeAPICall<
    API.CommandResponse,
    EmailPreferences,
    API.CommandResponse
>(
    ({ allowEmails }, state) => ({
        url: `${API_URL}${USERS_ROUTE}/${state?.auth?.user?.object?.id ?? ''}/`,
        requestData: {
            method: 'PATCH',
            body: JSON.stringify({
                profile: {
                    allow_emails: allowEmails,
                },
            }),
        },
    }),
    (results) => results,
);

export const updatePassword = makeAPICall<
    API.CommandResponse,
    UpdatePassword,
    API.CommandResponse
>(
    ({ old_password, password, confirm_password }) => ({
        url: `${API_URL}${SET_PASSWORD_ROUTE}`,
        requestData: {
            method: 'POST',
            body: JSON.stringify({
                old_password,
                password,
                confirm_password,
            }),
        },
    }),
    (results) => results,
);
